import React, { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import AddressInput from '@/components/common/AddressInput/AddressInput';
import Heading from '@/components/switchback/Heading/Heading';
import { ILocationSearchAutocompleteListOption } from '@/hooks/useLocationSearchAutocompleteOptions';
import {
  EDeliveryFields,
  EDeliveryOption,
  EDeliveryTypes,
  TDeliveryFields,
} from '@/services/types/core/delivery.types';
import { ILocation } from '@/services/types/search/rentals/id';

import { AfterDeliveryOptions } from './AfterDeliveryOptions';
import DeliveryModalOptions from './DeliveryModalOptions';

type IDeliveryFieldsElement = React.HTMLAttributes<HTMLElement>;

// Map EDeliveryTypes to EDeliveryOption
const chooseDeliveryOption = (
  type: EDeliveryTypes,
  movingOption: EDeliveryOption,
): EDeliveryOption => {
  if (type === EDeliveryTypes.PICKUP) return EDeliveryOption.PICKUP;
  if (type === EDeliveryTypes.DELIVERY) {
    if (movingOption === EDeliveryOption.STATIONARY) {
      return EDeliveryOption.STATIONARY;
    } else {
      return EDeliveryOption.MOVING;
    }
  }
  // Default to STATIONARY for delivery type
  return EDeliveryOption.STATIONARY;
};

const mapOptionToDeliveryType = (deliveryType: EDeliveryOption): EDeliveryTypes => {
  if (deliveryType === EDeliveryOption.PICKUP) {
    return EDeliveryTypes.PICKUP;
  }
  return EDeliveryTypes.DELIVERY;
};

interface IProps {
  form: UseFormReturn<TDeliveryFields>;
  location: ILocation;
  deliveryCostPerMile: string | undefined;
  deliveryType: EDeliveryOption;
  onChangeOption: (type: EDeliveryOption) => void;
  onInputChange: (inputValue: string) => void;
  onSelectAddress?: (value: ILocationSearchAutocompleteListOption) => void;
}

const DeliveryFields: React.FC<IProps & IDeliveryFieldsElement> = ({
  form,
  location,
  onInputChange,
  onSelectAddress,
  deliveryCostPerMile,
  deliveryType,
  onChangeOption,
}) => {
  const intl = useIntl();
  const addressValue = form.watch(EDeliveryFields.DELIVERY_ADDRESS) || '';

  const watchStationary = form.watch<EDeliveryFields>(
    EDeliveryFields.DELIVERY_STATIONARY,
    EDeliveryOption.MOVING,
  ) as EDeliveryOption;

  const [deliveryOption, setDeliveryOption] = useState(mapOptionToDeliveryType(deliveryType));

  useEffect(() => {
    const parsedDeliveryOption = chooseDeliveryOption(deliveryOption, watchStationary);

    // Call the parent's onChangeOption with the mapped delivery option
    onChangeOption(parsedDeliveryOption);
  }, [deliveryOption, onChangeOption, watchStationary]);

  const handleChangeOption = (value: EDeliveryTypes) => {
    setDeliveryOption(value);
  };

  return (
    <>
      <DeliveryModalOptions
        deliveryCostPerMile={deliveryCostPerMile}
        deliveryType={deliveryType}
        onChangeOption={handleChangeOption}
      />
      {(deliveryType === EDeliveryOption.MOVING || deliveryType === EDeliveryOption.STATIONARY) && (
        <>
          <AfterDeliveryOptions form={form} watchStationary={watchStationary} />
          <Heading level={3} className="mt-8 mb-4 autoType600 highlight">
            {intl.formatMessage({
              defaultMessage: 'Enter delivery address',
              id: 'lQZ2Ad',
            })}
          </Heading>
          <div className="mt-2 mb-3 md:mb-2">
            <AddressInput
              form={form}
              required={
                deliveryType === EDeliveryOption.MOVING ||
                deliveryType === EDeliveryOption.STATIONARY
              }
              addressValue={addressValue}
              name={EDeliveryFields.DELIVERY_ADDRESS}
              onChangeAddress={onInputChange}
              onSelectAddress={onSelectAddress}
              placesOption={{
                location: [location?.lng || 0, location?.lat || 0],
                types: 'address,region,place,district,locality,poi',
              }}
              label={intl.formatMessage({
                defaultMessage: 'Delivery location',
                id: 'Flx2cd',
              })}
              onFocus={e => e.target.select()}
            />
          </div>
        </>
      )}
    </>
  );
};

export default DeliveryFields;
